import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * Apple-like Button
 * - Softer shadows, subtle focus rings, gentle press states
 * - New `shape` (rounded | xl | 2xl | pill) and `fullWidth` props
 * - Backwards compatible variants: default, destructive, outline, secondary, ghost, link
 * - New variants: elevated, glass, subtle
 */

const buttonVariants = cva(
  [
    // base
    "inline-flex items-center justify-center gap-2 shrink-0 whitespace-nowrap",
    "font-medium text-sm select-none",
    "transition-all duration-200",
    "outline-none focus-visible:outline-none",
    // focus ring (soft, Apple-ish)
    "focus-visible:ring-2 focus-visible:ring-black/10 focus-visible:ring-offset-2 focus-visible:ring-offset-white",
    ":ring-white/20",
    // disabled
    "disabled:pointer-events-none disabled:opacity-50",
    // icons sizing
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0",
    // press/hover feel
    "active:translate-y-[1px]",
  ].join(" "),
  {
    variants: {
      variant: {
        // “System Blue” style but keeps theme primary; soft outer ring + tiny highlight
        default: [
          "bg-primary text-primary-foreground",
          "shadow-lg shadow-black/10 ring-1 ring-black/5",
          "hover:brightness-[.98] hover:shadow-xl",
          "active:shadow-md",
          "relative before:pointer-events-none before:absolute before:inset-0 before:rounded-inherit",
          "before:opacity-[0.0] hover:before:opacity-[0.08]",
          "before:transition-opacity before:bg-white",
        ].join(" "),
        // macOS destructive (kept)
        destructive: [
          "bg-destructive text-white",
          "shadow-lg shadow-black/10 ring-1 ring-black/5",
          "hover:brightness-95 active:shadow-md",
          "focus-visible:ring-destructive/30",
        ].join(" "),
        // thin outline on translucent surface
        outline: [
          "border border-black/10 bg-white/70 text-gray-900",
          "backdrop-blur-sm",
          "hover:bg-white",
          "shadow-sm",
          "",
        ].join(" "),
        // subtle filled secondary
        secondary: [
          "bg-gray-100 text-gray-900",
          "shadow-sm ring-1 ring-black/5",
          "hover:bg-gray-200 active:shadow-none",
          "",
        ].join(" "),
        // barely there
        ghost: [
          "bg-transparent text-gray-900 hover:bg-black/5",
          "",
        ].join(" "),
        link: "text-primary underline-offset-4 hover:underline",

        // New: elevated (white, card-like)
        elevated: [
          "bg-white text-gray-900",
          "ring-1 ring-black/5 shadow-lg shadow-black/10",
          "hover:shadow-xl active:shadow-md",
          "",
        ].join(" "),

        // New: glass (for frosted toolbars/segmented controls)
        glass: [
          "bg-white/70 backdrop-blur-xl text-gray-900",
          "ring-1 ring-black/5 shadow-md shadow-black/10",
          "hover:bg-white/80",
          "",
        ].join(" "),

        // New: subtle tonal (uses accent without heavy fill)
        subtle: [
          "text-primary bg-primary/10",
          "ring-1 ring-primary/20 shadow-sm",
          "hover:bg-primary/15",
          "",
        ].join(" "),
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-11 px-6 has-[>svg]:px-4",
        xl: "h-12 px-7 text-[15px] has-[>svg]:px-5",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-11",
      },
      shape: {
        rounded: "rounded-md",
        xl: "rounded-xl",
        "2xl": "rounded-2xl",
        pill: "rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      shape: "xl",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Make button full width */
  fullWidth?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      shape,
      asChild = false,
      fullWidth = false,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        data-slot="button"
        className={cn(
          buttonVariants({ variant, size, shape }),
          fullWidth && "w-full",
          className
        )}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
